import {
    ChangeDetectionStrategy,
    Component,
    computed,
    EventEmitter,
    input,
    InputSignal,
    Output,
    Signal,
} from '@angular/core';

export interface PageChangedEvent {
    page: number;
    itemsPerPage: number;
}

@Component({
    selector: 'app-pagination',
    templateUrl: './pagination.component.html',
    styleUrls: ['./pagination.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class PaginationComponent {
    totalItemsInput: InputSignal<number | null | undefined> = input<number | null | undefined>(0, { alias: 'totalItems' });
    itemsPerPageInput: InputSignal<number | null | undefined> = input<number | null | undefined>(10, { alias: 'itemsPerPage' });
    maxSizeInput: InputSignal<number | null | undefined> = input<number | null | undefined>(5, { alias: 'maxSize' });
    boundaryLinksInput: InputSignal<boolean | null | undefined> = input<boolean | null | undefined>(false, { alias: 'boundaryLinks' });
    pageInput: InputSignal<number | null | undefined> = input<number | null | undefined>(1, { alias: 'page' });

    @Output() pageChanged: EventEmitter<PageChangedEvent> = new EventEmitter<PageChangedEvent>();

    boundaryLinks: Signal<boolean> = computed(() => this.boundaryLinksInput() ?? false);

    totalItems: Signal<number> = computed(() => this.getSafeNonNegativeInt(this.totalItemsInput(), 0));
    itemsPerPage: Signal<number> = computed(() => this.getSafePositiveInt(this.itemsPerPageInput(), 1));

    totalPages: Signal<number> = computed(() => {
        const pages: number = Math.ceil(this.totalItems() / this.itemsPerPage());
        return Math.max(1, pages);
    });

    currentPage: Signal<number> = computed(() => {
        const page: number = this.getSafePositiveInt(this.pageInput(), 1);
        return this.clamp(page, 1, this.totalPages());
    });

    isFirstPage: Signal<boolean> = computed(() => this.currentPage() <= 1);
    isLastPage: Signal<boolean> = computed(() => this.currentPage() >= this.totalPages());

    pages: Signal<number[]> = computed(() => {
        const totalPages: number = this.totalPages();
        const currentPage: number = this.currentPage();
        const maxSize: number = this.clamp(this.getSafePositiveInt(this.maxSizeInput(), 5), 1, totalPages);

        if (totalPages <= maxSize) {
            return this.buildRange(1, totalPages);
        }

        const half: number = Math.floor(maxSize / 2);
        let start: number = currentPage - half;
        let end: number = start + maxSize - 1;

        if (start < 1) {
            start = 1;
            end = maxSize;
        }

        if (end > totalPages) {
            end = totalPages;
            start = totalPages - maxSize + 1;
        }

        return this.buildRange(start, end);
    });

    onGoToFirst(): void {
        this.emitPage(1);
    }

    onGoToPrevious(): void {
        this.emitPage(this.currentPage() - 1);
    }

    onGoToPage(page: number): void {
        this.emitPage(page);
    }

    onGoToNext(): void {
        this.emitPage(this.currentPage() + 1);
    }

    onGoToLast(): void {
        this.emitPage(this.totalPages());
    }

    private emitPage(page: number): void {
        const nextPage: number = this.clamp(this.getSafePositiveInt(page, 1), 1, this.totalPages());
        if (nextPage === this.currentPage()) {
            return;
        }

        this.pageChanged.emit({
            page: nextPage,
            itemsPerPage: this.itemsPerPage(),
        });
    }

    private buildRange(start: number, end: number): number[] {
        const length: number = end - start + 1;
        if (length <= 0) {
            return [];
        }

        return Array.from({ length }, (_, idx: number) => start + idx);
    }

    private clamp(value: number, min: number, max: number): number {
        return Math.min(Math.max(value, min), max);
    }

    private getSafePositiveInt(value: number | null | undefined, fallback: number): number {
        if (typeof value !== 'number' || !Number.isFinite(value)) {
            return fallback;
        }

        const intValue: number = Math.floor(value);
        return intValue > 0 ? intValue : fallback;
    }

    private getSafeNonNegativeInt(value: number | null | undefined, fallback: number): number {
        if (typeof value !== 'number' || !Number.isFinite(value)) {
            return fallback;
        }

        const intValue: number = Math.floor(value);
        return intValue >= 0 ? intValue : fallback;
    }
}

